// frontend/src/pages/ForecastGenerator.jsx
//
// "Run forecasts on demand": starts the store's forecast run (the scheduled
// batch's own path, forecasting/views.py run_batch), follows its progress, then
// pages the run's results from the server, one row per item-location.

import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTabs } from '../tabs/useTabs';
import { useFormatters } from '../hooks/useFormatters';
import { useForecastStatistics } from '../hooks/useForecastData';
import { useSsrmGrid } from '../hooks/useSsrmGrid';
import { sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { apiJson, translateRequestError } from '../queries/api';
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useAgGridTheme } from '../utils/agGridTheme';
import GridIconAction from '../components/grid/GridIconAction';
import { GridEmptyState } from '../components/grid/NoRowsOverlay';
import { useIsShopify, itemIdentityColumns } from '../components/grid/productIdentity';
import { useProductGrouping } from '../components/grid/productGrouping';
import { defaultColDef, defaultGridProps, copyOnlyContextMenuItems, iconActionColumn, flexColumn } from '../utils/gridDefaults';
import { useColumnTypes } from '../hooks/useColumnTypes';
import {
  Typography,
  Stack,
  Button,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ICON, CaretDown, ChartLine, Info, X } from '../icons';
import StatusChip from '../components/common/StatusChip';
import ChipSlot from '../components/grid/ChipSlot';
import DialogHeading from '../components/common/DialogHeading';
import PageBanner from '../components/common/PageBanner';
import Tile from '../components/layout/Tile';
import ForecastChart from '../components/forecastchart/ForecastChart';
import { forecastChartRows } from './forecastChartRows';
import '../styles/pages.css';
import { showToast } from '../components/common/appToastBus';
import { GridPage, GridViewport } from '../components/grid/GridPage';

const RESULTS_ENDPOINT = '/api/forecast-generator/results/';
const NO_HIDDEN_COLUMNS = [];

const MODEL_INFO = {
  prophet: { display: 'Prophet' },
  autoets_no_trend: { display: 'AutoETS (no trend)' },
  autoets_with_trend: { display: 'AutoETS (with trend)' },
  autoarima: { display: 'AutoARIMA' },
  autoarima_seasonal: { display: 'AutoARIMA (seasonal)' },
  croston_sba: { display: 'Croston (SBA)' },
  croston_optimized: { display: 'Croston (optimized)' },
  simple_last_month: { display: 'Simple Last Month' },
  simple_moving_average: { display: 'Simple Moving Avg' }
};


function ForecastGenerator() {
  const { t } = useTranslation();
  const { openTab } = useTabs();
  const { formatNumber, formatMonthYear } = useFormatters();
  // Reminder banner — a run started here does not recalculate safety stock,
  // while every scheduled pipeline runs its stock-calculation step after its
  // forecast steps (core/scheduling/steps.py stock_calculation_step). Without
  // a nudge, classification (and therefore SystemSafetyStock) can lag the
  // freshly-generated forecasts. The X hides it until the next
  // generation finishes — the tab stays mounted, so a dismissal would
  // otherwise outlive the run the reminder is about.
  const [ssReminderDismissed, setSsReminderDismissed] = useState(false);

  const getModelStatusChip = (model) => {
    if (model.status === 'skipped') return <StatusChip label={t('forecastGenerator.statusSkipped')} color="neutral" />;
    if (model.status === 'error') return <StatusChip label={t('forecastGenerator.statusError')} color="error" />;
    return <StatusChip label={t('forecastGenerator.statusSuccess')} color="success" />;
  };

  const getStockoutFlagLabel = (flag) => {
    const labels = {
      original: t('forecastGenerator.flagNoAdjustment'),
      adjusted: t('forecastGenerator.flagCorrected'),
      adjustment_disabled: t('forecastGenerator.flagDisabled'),
      insufficient_inventory_data: t('forecastGenerator.flagLowCoverage'),
    };
    return labels[flag] || flag;
  };

  // The display name of a `selected_model` / `model_name`. `insufficient_data`
  // is not a model: the engine stores a zero forecast under that name when
  // there was nothing to fit (forecast_engine/service.py run_forecast), so it
  // reads as the status it is. A model name this page does not know is shown as-is —
  // visibly, not blanked.
  const modelLabel = useCallback((model) => (
    model === 'insufficient_data'
      ? t('forecastGenerator.statusInsufficientData')
      : MODEL_INFO[model]?.display || model
  ), [t]);

  // Settings states
  const [enableStockoutAdjustment, setEnableStockoutAdjustment] = useState(true);

  // Grid states. The grid shows the run this page started last, by its id.
  const [batchId, setBatchId] = useState(null);
  const [runningGeneration, setRunningGeneration] = useState(false);
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const isShopify = useIsShopify();
  const grouping = useProductGrouping({
    gridId: 'forecast_generator',
    preferenceKey: 'forecast_generator_grouped',
    defaultHidden: NO_HIDDEN_COLUMNS,
    leafIdFields: ['id'],
  });
  const { isGrouped, columns: groupedColumns, variantProductColumn, isGroupRow } = grouping;
  // The rows come in the server's one order (forecasting/models.py
  // forecast_run_results), so no column offers a sort.
  const gridDefaultColDef = useMemo(
    () => ({ ...defaultColDef, ...grouping.defaultColDef, sortable: false }),
    [grouping.defaultColDef],
  );
  const ssrmParams = useMemo(() => ({ batch_id: batchId }), [batchId]);
  const { onGridReady, onGridPreDestroyed, loading: loadingRows, error: rowsError, pagination } = useSsrmGrid({
    endpoint: RESULTS_ENDPOINT,
    params: ssrmParams,
    enabled: batchId != null,
  });

  // Statistics — TanStack-cached; navigating away and back within 30s reuses
  // the cached values.
  const { data: statistics, loading: statsLoading, error: statsError } = useForecastStatistics();

  // Progress tracking
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // The details dialog: the clicked row (it names the item and location) and
  // the run's result for it, read when the row is clicked.
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const selectedResult = selected?.result ?? null;

  // Status cell renderer — a dependency of the memoized columnDefs below, so
  // it must be stable itself.
  const StatusCellRenderer = useCallback((props) => {
    // A product row sums its variants and has no status of its own.
    if (isGroupRow(props.data)) return null;
    // Each result row has one of three statuses (forecasting/serializers.py
    // ForecastRunRowSerializer: success or insufficient_data where the run
    // wrote a result, error where it wrote none). Anything else is a broken
    // contract, not a status to print raw.
    const statusMap = {
      success: { label: t('forecastGenerator.statusSuccess'), color: 'success' },
      insufficient_data: { label: t('forecastGenerator.statusInsufficientData'), color: 'warning' },
      error: { label: t('forecastGenerator.statusError'), color: 'error' },
    };
    const status = statusMap[props.value];
    if (!status) throw new Error(`ForecastGenerator: unknown row status "${props.value}"`);
    const chip = <StatusChip label={status.label} color={status.color} />;
    // A failed row names what went wrong on hover.
    const shown = props.value === 'error'
      ? <Tooltip title={t('forecastGenerator.rowFailed')}>{chip}</Tooltip>
      : chip;
    const alternatives = Object.entries(statusMap)
      .filter(([value]) => value !== props.value)
      .map(([value, { label }]) => <StatusChip key={value} label={label} />);
    return <ChipSlot shown={shown} alternatives={alternatives} />;
  }, [t, isGroupRow]);

  const openForecastDetails = useCallback(async (row) => {
    try {
      const result = await apiJson.get(`/api/forecast-generator/${row.result_id}/`);
      setSelected({ row, result });
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('forecast-generator result:', error);
      showToast(translateRequestError(error, t, 'forecastGenerator.failedToLoadDetails'), 'error');
    }
  }, [t]);

  // AG Grid column definitions — memoized, as LocationSetup and PromotionForm
  // build theirs.
  const columnDefs = useMemo(() => [
    iconActionColumn({
      cellRenderer: GridIconAction,
      cellRendererParams: {
        icon: ChartLine,
        label: t('forecastGenerator.forecastDetails'),
        onClick: openForecastDetails,
        // A pair the run wrote no result for has nothing to chart and no
        // model to name; the status cell already says it failed. A product
        // row carries no result either.
        when: (row) => row.result_id != null,
      },
      lockPosition: 'left',
    }),
    ...groupedColumns([variantProductColumn()]),
    ...(isGrouped ? [] : itemIdentityColumns(isShopify, {
      productHeader: t('forecastGenerator.columns.item'),
      codeHeader: t('forecastGenerator.columns.itemCode'),
      nameHeader: t('forecastGenerator.columns.itemName'),
    })),
    {
      field: "location_name",
      headerName: t('forecastGenerator.columns.location'),
    },
    {
      field: "selected_model",
      headerName: t('forecastGenerator.columns.model'),
      ...flexColumn(),
      valueFormatter: (params) => {
        if (!params.value) return '—';
        return modelLabel(params.value);
      }
    },
    {
      field: "mape",
      headerName: t('forecastGenerator.columns.mape'),
      type: 'decimalColumn',
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return '—';
        return formatNumber(params.value, 2);
      }
    },
    {
      field: "bias",
      headerName: t('forecastGenerator.columns.bias'),
      type: 'decimalColumn',
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return '—';
        return formatNumber(params.value, 2);
      }
    },
    {
      field: "execution_time_seconds",
      headerName: t('forecastGenerator.columns.time'),
      type: 'decimalColumn',
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return '—';
        return formatNumber(params.value, 2);
      }
    },
    {
      field: "status",
      headerName: t('forecastGenerator.columns.status'),
      type: 'centeredColumn',
      cellRenderer: StatusCellRenderer,
    }
  ], [t, formatNumber, modelLabel, StatusCellRenderer, openForecastDetails,
    groupedColumns, variantProductColumn, isGrouped, isShopify]);

  const handleGenerateForecasts = async () => {
    setRunningGeneration(true);
    // The stream's progress frames carry the total; the bar waits for the first.
    setProgress({ current: 0, total: 0 });

    try {
      // sessionApi handles X-Store-ID, X-CSRFToken, credentials automatically
      const response = await sessionApi.post('/api/forecast-generator/run-batch/', {
        enable_stockout_adjustment: enableStockoutAdjustment,
      });

      if (!response.ok) {
        throw await apiErrorFrom(response, 'POST /api/forecast-generator/run-batch/');
      }

      // Every frame the view writes is one JSON object and a newline, and
      // the stream ends on a `complete` or an `error` frame
      // (forecasting/views.py run_batch). A chunk can end mid-frame.
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let completed = false;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          const message = JSON.parse(line);
          if (message.type === 'progress') {
            setProgress({ current: message.current, total: message.total });
          } else if (message.type === 'complete') {
            completed = true;
            setBatchId(message.batch_id);
            setSsReminderDismissed(false);
            const { success, error } = message.summary;
            if (error > 0) {
              showToast(t('forecastGenerator.someFailed', { count: error }), 'warning');
            } else {
              showToast(t('forecastGenerator.successGenerated', { count: success }), 'success');
            }
          } else if (message.type === 'error') {
            throw new Error(message.error);
          } else {
            throw new Error(`Unknown run-batch frame type: ${message.type}`);
          }
        }
      }
      if (!completed) throw new Error('The run-batch stream ended without a complete frame');
    } catch (error) {
      // A refused start is an ApiError and a request that never left a
      // NetworkError; an error frame, a frame that is not JSON and a stream
      // cut short are the server failing. translateRequestError picks the
      // next step for each cause.
      console.error('forecast-generator run-batch:', error);
      showToast(translateRequestError(error, t, 'forecastGenerator.failedToGenerate'), 'error');
    } finally {
      setRunningGeneration(false);
    }
  };

  return (
    <div className="page-container">
      {!ssReminderDismissed && (
        <PageBanner
          severity="info"
          messages={[t('forecastGenerator.runSafetyStockReminder')]}
          actions={[{
            label: t('forecastGenerator.runSafetyStockAction'),
            onClick: () => openTab('/safety-stock-generator'),
          }]}
          onDismiss={() => setSsReminderDismissed(true)}
        />
      )}
      <PageBanner
        severity="error"
        messages={[rowsError && translateRequestError(rowsError, t, 'forecastGenerator.failedToLoadResults')]}
      />
      {/* Configuration & Overview Section */}
      <div className="tiles-row">
        <Tile width="full" title={t('forecastGenerator.configTitle')}>
          <Stack spacing={3}>
            {/* Statistics Section */}
            {statsError && <Alert severity="error">{statsError}</Alert>}
            {!statsLoading && statistics && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('forecastGenerator.databaseOverview')}
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <StatusChip label={t('forecastGenerator.activeItems', { count: statistics.items.active })} color="success" />
                  <StatusChip label={t('forecastGenerator.inactiveItems', { count: statistics.items.inactive })} color="neutral" />
                  <StatusChip label={t('forecastGenerator.locations', { count: statistics.locations.total })} color="info" />
                  <StatusChip label={t('forecastGenerator.forecastableCombinations', { count: statistics.combinations.forecastable })} color="info" />
                </Stack>
              </Box>
            )}

            {/* Settings Section */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('forecastGenerator.forecastSettings')}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }} className="generator-settings-row">
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={enableStockoutAdjustment}
                      onChange={(e) => setEnableStockoutAdjustment(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={t('forecastGenerator.enableStockout')}
                />
              </Stack>
            </Box>
          </Stack>
        </Tile>
      </div>

      {/* Forecasts Grid */}
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('forecastGenerator.forecastResults')}
          toolbar={
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              {grouping.toggle}
              <Button
                variant="contained"
                onClick={handleGenerateForecasts}
                disabled={runningGeneration}
                loading={runningGeneration}
              >
                {t('forecastGenerator.generateForecasts')}
              </Button>
            </Stack>
          }
        >
          <GridPage>
            {runningGeneration && (
              <div className="grid-page-loading">
                <CircularProgress size={40} />
                <Typography variant="h6" className="grid-page-loading-text">
                  {t('forecastGenerator.generatingForecasts')}
                </Typography>
                {progress.total > 0 && (
                  <Box sx={{ width: '100%', maxWidth: 400, mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('forecastGenerator.progress', { current: progress.current, total: progress.total })}
                    </Typography>
                    <Box sx={{
                      width: '100%',
                      height: 8,
                      backgroundColor: 'var(--color-border)',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}>
                      <Box sx={{
                        width: `${(progress.current / progress.total) * 100}%`,
                        height: '100%',
                        backgroundColor: 'var(--color-primary)',
                        transition: 'width 0.3s ease'
                      }} />
                    </Box>
                  </Box>
                )}
              </div>
            )}

            {!runningGeneration && (
              <GridViewport>
                {gridTheme && (
                  <GridEmptyState
                    title={t('forecastGenerator.empty.title')}
                    message={t('forecastGenerator.empty.message', { action: t('forecastGenerator.generateForecasts') })}
                  >
                    <AgGridReact
                      {...defaultGridProps}
                      theme={gridTheme}
                      columnDefs={columnDefs}
                      defaultColDef={gridDefaultColDef}
                      columnTypes={columnTypes}
                      loading={loadingRows}
                      {...grouping.gridProps}
                      rowModelType="serverSide"
                      {...pagination}
                      onGridReady={onGridReady}
                      onGridPreDestroyed={onGridPreDestroyed}
                      getContextMenuItems={copyOnlyContextMenuItems}
                    />
                  </GridEmptyState>
                )}
              </GridViewport>
            )}

          </GridPage>
        </Tile>
      </div>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        className="dialog-form dialog-form--wide forecast-chart-modal"
      >
        <DialogTitle className="forecast-chart-modal-header">
          <Info className="forecast-chart-modal-icon" />
          <DialogHeading
            title={t('forecastGenerator.forecastDetails')}
            subtitle={selected && t('forecastGenerator.detailsSubtitle', { item: selected.row.item_code, location: selected.row.location_name })}
          />
          <IconButton
            onClick={() => setDetailDialogOpen(false)}
            size="small"
            aria-label={t('btn.close')}
          >
            <X size={ICON.xxl} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedResult && (
            <>
              <ForecastChart
                statisticalForecast={forecastChartRows(selectedResult)}
                height={450}
              />

              {/* Summary Chips - Below chart */}
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <StatusChip label={t('forecastGenerator.selectedModelChip', { model: modelLabel(selectedResult.selected_model) })} color="info" />
                {selectedResult.mape != null && (
                  <StatusChip label={t('forecastGenerator.mapeChip', { value: formatNumber(selectedResult.mape, 2) })} color="info" />
                )}
                {selectedResult.bias != null && (
                  <StatusChip label={t('forecastGenerator.biasChip', { value: formatNumber(selectedResult.bias, 2) })} color="info" />
                )}
                <StatusChip label={t('forecastGenerator.runTimeChip', { seconds: formatNumber(selectedResult.execution_time_seconds, 2) })} color="neutral" />
              </Stack>

              {/* Model Performance */}
              {selectedResult.model_performances?.length > 0 && (
                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<CaretDown size={ICON.xl} />}>
                    <Typography>
                      {t('forecastGenerator.modelPerformance')} ({selectedResult.model_performances.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('forecastGenerator.columns.modelName')}</TableCell>
                          <TableCell align="right">{t('forecastGenerator.columns.mape')}</TableCell>
                          <TableCell align="right">{t('forecastGenerator.columns.bias')}</TableCell>
                          <TableCell align="right">{t('forecastGenerator.columns.mae')}</TableCell>
                          <TableCell>{t('forecastGenerator.status')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedResult.model_performances.map((model, idx) => (
                          <TableRow key={idx} className={model.is_selected ? 'model-performance-row-selected' : ''}>
                            <TableCell>
                              {modelLabel(model.model_name)}
                              {model.is_selected && (
                                <> <StatusChip label={t('forecastGenerator.bestFit')} color="info" /></>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {model.status === 'skipped' ? '—' : formatNumber(model.mape, 2)}
                            </TableCell>
                            <TableCell align="right">
                              {model.status === 'skipped' ? '—' : formatNumber(model.bias, 2)}
                            </TableCell>
                            <TableCell align="right">
                              {model.status === 'skipped' ? '—' : formatNumber(model.mae, 2)}
                            </TableCell>
                            <TableCell>{getModelStatusChip(model)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Stockout Summary */}
              {selectedResult.stockout_summary && (
                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<CaretDown size={ICON.xl} />}>
                    <Typography>{t('forecastGenerator.stockoutAnalysis')}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <StatusChip
                          label={selectedResult.stockout_summary.adjustment_enabled ?
                            t('forecastGenerator.adjustmentEnabled') : t('forecastGenerator.adjustmentDisabled')}
                          color={selectedResult.stockout_summary.adjustment_enabled ? 'info' : 'neutral'}
                        />
                        <StatusChip label={`${selectedResult.stockout_summary.months_original} ${t('forecastGenerator.monthsNoAdjustment')}`} color="neutral" />
                        {selectedResult.stockout_summary.months_adjusted > 0 && (
                          <StatusChip label={t('forecastGenerator.correctedCount', { count: selectedResult.stockout_summary.months_adjusted })} color="info" />
                        )}
                        {selectedResult.stockout_summary.months_insufficient_data > 0 && (
                          <StatusChip label={t('forecastGenerator.lowCoverageCount', { count: selectedResult.stockout_summary.months_insufficient_data })} color="info" />
                        )}
                      </Stack>

                      {selectedResult.stockout_summary.monthly_details?.length > 0 && (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('forecastGenerator.month')}</TableCell>
                              <TableCell align="right">{t('forecastGenerator.rawSales')}</TableCell>
                              <TableCell align="right">{t('forecastGenerator.adjustedSales')}</TableCell>
                              <TableCell>{t('forecastGenerator.adjustmentType')}</TableCell>
                              <TableCell align="right">{t('forecastGenerator.severity')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedResult.stockout_summary.monthly_details.map((month, idx) => (
                              <TableRow
                                key={idx}
                                className={
                                  month.stockout_flag === 'adjusted'
                                    ? 'stockout-table-row-adjusted'
                                    : ''
                                }
                              >
                                <TableCell>{formatMonthYear(month.Month)}</TableCell>
                                <TableCell align="right">{formatNumber(month.raw_sales, 0)}</TableCell>
                                <TableCell align="right">
                                  <strong>{formatNumber(month.adjusted_sales, 0)}</strong>
                                </TableCell>
                                <TableCell>
                                  <StatusChip
                                    label={getStockoutFlagLabel(month.stockout_flag)}
                                    color={
                                      month.stockout_flag === 'original' || month.stockout_flag === 'adjustment_disabled'
                                        ? 'neutral'
                                        : 'info'
                                    }
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <StatusChip label={`${formatNumber(month.stockout_severity, 1)}%`} color="neutral" />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)} variant="outlined">
            {t('forecastGenerator.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ForecastGenerator;